import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import { UserPlus, UserCheck, BadgeCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InfluencerStrip() {
  const [influencers, setInfluencers] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [followStates, setFollowStates] = useState({});
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWithRetry = async (retries = 2) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const r = await axios.get(`${API}/influencers`);
          setInfluencers(r.data);
          return;
        } catch (err) {
          if (i < retries) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
        }
      }
    };
    fetchWithRetry();
  }, []);

  // Check follow state for each influencer
  useEffect(() => {
    if (!isAuthenticated || influencers.length === 0) return;
    influencers.forEach(inf => {
      if (inf.user_id !== user?.user_id) {
        axios.get(`${API}/follows/check/${inf.user_id}`)
          .then(r => setFollowStates(prev => ({ ...prev, [inf.user_id]: r.data.following })))
          .catch(() => {});
      }
    });
  }, [isAuthenticated, influencers, user?.user_id]);

  const handleFollow = async (e, userId) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    try {
      const res = await axios.post(`${API}/follows/${userId}`, {});
      setFollowStates(prev => ({ ...prev, [userId]: res.data.following }));
    } catch { /* ignore */ }
  };

  if (influencers.length === 0) return null;

  return (
    <>
      <section className="mb-4" data-testid="influencer-strip">
        <h2 className="font-heading text-lg sm:text-xl text-[#2C2A29] mb-3">Beauty Creators</h2>
        <div className="flex overflow-x-auto gap-4 pb-3 hide-scrollbar">
          {influencers.map((inf) => {
            const isSelf = user?.user_id === inf.user_id;
            const isFollowing = followStates[inf.user_id] || false;
            return (
              <div
                key={inf.user_id}
                className="flex-shrink-0 flex flex-col items-center cursor-pointer group"
                onClick={() => navigate(`/profile/${inf.user_id}`)}
                data-testid={`influencer-card-${inf.user_id}`}
              >
                <div className="relative">
                  <img
                    src={inf.picture || 'https://via.placeholder.com/96'}
                    alt={inf.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white shadow-sm object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {inf.influencer_verified && (
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-blue-100 shadow-sm" data-testid={`verified-badge-${inf.user_id}`}>
                      <BadgeCheck size={16} className="text-blue-500 fill-blue-50" />
                    </div>
                  )}
                </div>
                <p className="text-sm font-body font-medium text-[#2C2A29] text-center mt-3 whitespace-nowrap">{inf.name}</p>
                {inf.is_demo ? (
                  <p className="text-xs font-body text-amber-600 text-center font-medium" data-testid={`demo-influencer-${inf.user_id}`}>Demo influencer</p>
                ) : (
                  <p className="text-xs font-body text-[#78716C] text-center">
                    {inf.social_platforms?.[0] && <span className="text-[#C5A880]">{inf.social_platforms[0]}</span>}
                    {inf.social_platforms?.[0] && inf.follower_range && <span className="text-[#A89F91]"> · </span>}
                    {inf.follower_range && <span className="text-[#A89F91]">{inf.follower_range}</span>}
                  </p>
                )}
                {!isSelf && (
                  <button
                    onClick={(e) => handleFollow(e, inf.user_id)}
                    className={`mt-2 rounded-full px-4 py-1 font-body text-xs font-semibold transition-all flex items-center gap-1.5 ${
                      isFollowing
                        ? 'bg-[#FFF5F5] text-[#D48585] border border-[#F4C2C2]'
                        : 'border border-cream-100 bg-transparent text-[#2C2A29] hover:border-[#F4C2C2] hover:bg-[#FFF5F5]'
                    }`}
                    data-testid={`follow-btn-${inf.user_id}`}
                  >
                    {isFollowing ? <><UserCheck size={12} /> Following</> : <><UserPlus size={12} /> Follow</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </>
  );
}
